<!--
  - /*===========================================================================
  - [StrongGate Limited] [http://www.stronggate.com]
  - Copyright (c) 2025. All rights Reserved.
  - Unauthorized access, use, dissemination, distribution, reproduction of this
  - file, or a portion of, in any form is prohibited and unlawful. If you receive
  - this file, and you are not an authorized company employee, or you believe you
  - have received this file in error, please notify the sender (or originator)
  - and promptly delete this file.
  -
  - Project:
  - File: side-nav.vue
  - Created by: chan.ying
  - Create Date: 2025-06-12 19:06
  - Version Number: 1.0
  - Version Description:
  -
  - =============================================================================*/
  -->

<template>
  <div class="sidebar ztn-page-h500" :class="{ collapsed: isCollapse }">
    <div class="menu-title ztn-page-h200-strong">MENU</div>
    <ul class="menu">
      <template v-for="menuItem in menuItems" :key="menuItem.id">
        <li v-if="CheckUtil.isNull(menuItem.subItems)" class="menu-item">
          <router-link
              :to="menuItem.link"
              class="menu-link"
              :class="{ active: isActive(menuItem) && !hasActiveSubMenu() }"
          >
            <span class="icon">
              <component
                  v-if="menuItem && menuItem.icon > 0"
                  :is="getIconComponent(menuItem.icon)"
                  :stroke-color="isActive(menuItem) && !hasActiveSubMenu()
                 ? 'var(--sidebar-primary)' : 'var(--sidebar-secondary)'"
              />
            </span>
            <span class="label"
                  v-if="!isCollapse"
                  :style="{color:isActive(menuItem) && !hasActiveSubMenu()
                   ? 'var(--sidebar-primary)' : 'var(--sidebar-secondary)'}">
              {{ $t(menuItem.label) }}
            </span>
          </router-link>
        </li>

        <li v-else class="menu-item"
            @mouseenter="onMenuHover(menuItem.id, $event)"
            @mouseleave="onMenuLeave">
          <div
              class="menu-header"
              :class="{ active: isParentActive(menuItem) }"
              @click="toggleSubMenu(menuItem.type, menuItem.id)"
          >
            <span class="icon">
              <component
                  v-if="menuItem && menuItem.icon > 0"
                  :is="getIconComponent(menuItem.icon)"
                  :stroke-color="isParentActive(menuItem)
                 ? 'var(--sidebar-primary)' : 'var(--sidebar-secondary)'"
              />
            </span>
            <span class="label" v-if="!isCollapse">
              {{ $t(menuItem.label) }}
            </span>
            <img class="arrow"
                 :class="{ rotated: openMenuIds.includes(menuItem.id) }"
                 src="@/assets/icons/menu/chevron-up.svg"
                 alt=""
                 v-if="!isCollapse"
            />
          </div>

          <div v-if="isCollapse && openMenuIds.includes(menuItem.id)">
            <ul class="submenu-hover" :style="{ top: `${hoveredMenuTop}px` }">
              <SidebarMenuItem
                  v-for="subItem in menuItem.subItems"
                  :key="subItem.id"
                  :item="subItem"
                  :level="1.2"
                  :on-load-more="onClickLoadMore"
              />
            </ul>
          </div>

          <div v-else>
            <ul v-if="openMenuIds.includes(menuItem.id)" class="submenu">
              <SidebarMenuItem
                  v-for="subItem in menuItem.subItems"
                  :key="subItem.id"
                  :item="subItem"
                  :level="1.2"
                  :on-load-more="onClickLoadMore"
              />
            </ul>
          </div>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import DashboardSvg from "@/assets/icons/menu/dashboard";
import TeamSvg from "@/assets/icons/menu/team-svg";
import NetworkAccessSvg from "@/assets/icons/menu/network-access";
import SecureAccessSvg from "@/assets/icons/menu/secure-access";
import DrivesSvg from "@/assets/icons/menu/drives";
import SettingsSvg from "@/assets/icons/menu/settings";
import GeoProxy from "@/assets/icons/menu/geo-proxy.vue";
import CheckUtil from "@/commons/utils/CheckUtil";
import SidebarMenuItem from "@/components/sidebar-menu-item.vue";
import Log from "@/commons/logs/Log";
import OnlineUserManager from "@/security/OnlineUserManager";

export default {
  components: {
    SidebarMenuItem,
    GeoProxy,
    DashboardSvg,
    TeamSvg,
    NetworkAccessSvg,
    SecureAccessSvg,
    DrivesSvg,
    SettingsSvg
  },
  props: {
    isCollapse: Boolean
  },
  data() {
    return {
      openMenuIds: [],
      hoveredMenuTop: 0,
      value: null,
    };
  },
  mounted() {
    this.value = this.$route.path;
    this.setOpenMenuId();
  },
  methods: {
    onClickLoadMore(parentSubItem) {
      const all = parentSubItem._allItems || [];
      const current = parentSubItem._visibleCount || 0;

      if (parentSubItem.subItems.length > 0) {
        const last = parentSubItem.subItems[parentSubItem.subItems.length - 1];
        if (last && last.id === '__more_units__') {
          parentSubItem.subItems.pop();
        }
      }

      const nextCount = Math.min(current + 5, all.length); // dynamic menu
      const nextSlice = all.slice(current, nextCount);
      parentSubItem.subItems.push(...nextSlice);
      parentSubItem._visibleCount = nextCount;

      if (nextCount < all.length) {
        parentSubItem.subItems.push(this.buildLoadMoreItem(parentSubItem));
      }
    },

    setOpenMenuId() {
      this.openMenuIds = [];
      this.menuItems.forEach((menuItem) => {
        if (CheckUtil.isNotNull(menuItem.subItems) && menuItem.subItems.length > 0) {
          const currentPrefix = this.getLinkPrefix(this.$route.path);
          const isSubItemActive = menuItem.subItems.some(
              (subItem) => currentPrefix === this.getLinkPrefix(subItem.link)
          );
          if (isSubItemActive) {
            this.openMenuIds.push(menuItem.id);
          }
        }
      });
    },

    getLinkPrefix(path) {
      const parts = path.split('/');
      return parts.length > 1 ? `/${parts[1]}` : '/';
    },

    isActive(item) {
      if (item.type === 1) return false;
      const currentPrefix = this.getLinkPrefix(this.$route.path);
      const linkPrefix = this.getLinkPrefix(item.link);
      return currentPrefix === linkPrefix || this.$route.path === item.link;
    },

    isParentActive(menuItem) {
      if (CheckUtil.isNotNull(menuItem.subItems) && menuItem.subItems.length > 0) {
        return menuItem.subItems.some((subItem) => this.isActive(subItem));
      }
      return this.isActive(menuItem);
    },

    hasActiveSubMenu() {
      return this.menuItems.some(menuItem =>
          CheckUtil.isNotNull(menuItem.subItems) &&
          menuItem.subItems.some(subItem => this.isActive(subItem))
      );
    },

    toggleSubMenu(type, menuId) {
      Log.debug(type);
      Log.debug(menuId);
      const index = this.openMenuIds.indexOf(menuId);
      if (index > -1) {
        this.openMenuIds.splice(index, 1);
      } else {
        this.openMenuIds.push(menuId);
      }
    },
    getIconComponent(iconId) {
      switch (iconId) {
        case 1:
          return "DashboardSvg";
        case 2:
          return "TeamSvg";
        case 3:
          return "NetworkAccessSvg";
        case 4:
          return "SecureAccessSvg";
        case 5:
          return "GeoProxy";
        case 6:
          return "DrivesSvg";
        case 7:
          return "SettingsSvg";
        default:
          return "DashboardSvg";
      }
    },

    onMenuHover(menuId, event) {
      if (this.isCollapse) {
        this.openMenuIds = [menuId];
        const menuItem = event.currentTarget;
        const rect = menuItem.getBoundingClientRect();
        this.hoveredMenuTop = rect.top;
      }
    },

    onMenuLeave() {
      if (this.isCollapse) {
        this.openMenuIds = [];
      }
    },
  },
  watch: {
    $route(to) {
      this.setOpenMenuId();
      let str = window.location.hash;
      const regex = /#\/(.*?)\//;
      const matches = str.match(regex);
      if (CheckUtil.isNotEmpty(matches) && to.path.includes(matches[1])) {
        this.value = '/' + matches[1] + '/list';
      }
    },
    isCollapse(newValue) {
      if (newValue) {
        this.openMenuIds = [];
        this.hoveredMenuTop = 0;
      } else {
        this.setOpenMenuId();
      }
    },
  },
  computed: {
    CheckUtil() {
      return CheckUtil
    },
    menuItems() {
      return OnlineUserManager.Instance.get().userInfo.availableMenus;
    }
  }
};
</script>

<style scoped>
.sidebar {
  /*background-color: var(--sidebar-background);*/
  position: relative;
  /*width: 240px;*/
  transition: width 0.2s;
}

.sidebar.collapsed {
  position: fixed;
  left: 0;
  top: 40px;
  width: 70px;
  height: 100vh;
  z-index: 100;
}

.menu-title {
  color: var(--sidebar-secondary);
  text-transform: none !important;
}

.menu {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  margin-bottom: var(--space-3);
}

.menu-link,
.menu-header {
  display: flex;
  align-items: center;
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  position: relative;
  transition: background-color 0.3s, color 0.3s;
}

.label {
  flex-grow: 1;
}

.arrow {
  transition: transform 0.3s ease;
  margin-left: auto;
}

.arrow.rotated {
  transform: rotate(180deg);
}

.menu-link.active {
  color: var(--sidebar-primary);
  font-weight: bold;
}

.menu-header.active {
  color: var(--sidebar-primary);
}

.menu-link .icon,
.menu-header .icon {
  margin-right: var(--space-4);
  margin-bottom: var(--space-3);
}

.submenu {
  list-style: none;
  padding: 0;
}

.submenu-item {
  margin-bottom: var(--space-1);
}

.submenu-label {
  margin-left: var(--space-6);
}

/* *********************************** */
/* Note: These are forbidden to delete */
/* *********************************** */
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.7s ease;
}

.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
}

.submenu-enter-to,
.submenu-leave-from {
  max-height: 500px;
  opacity: 1;
}

.submenu-hover {
  position: fixed;
  left: 70px;
  background-color: var(--sidebar-background);
  box-shadow: var(--elevation-sm);
  border-radius: var(--radius-xs);
  z-index: 1000;
  padding: var(--space-3) 0;
  list-style: none;
  width: 200px;
}

.submenu-hover .submenu-item {
  margin-bottom: 0;
}

.submenu-hover .submenu-link {
  padding: var(--space-2) var(--space-4);
  text-decoration: none;
  color: var(--sidebar-secondary);
  font-size: var(--fs-14);
  transition: background-color 0.3s, color 0.3s;
}

.submenu-hover .submenu-link.active {
  color: var(--sidebar-primary);
  font-weight: bold;
}
</style>
